export type CompositionLine = { code: string; label: string; unit: string; quantity: number; unit_price: number; partial: number };
export type CompositionEdit = { code: string; quantity: string; unit_price: string };
export function compositionEdits(lines: CompositionLine[]): CompositionEdit[] {
  return lines.map((line) => ({ code: line.code, quantity: String(line.quantity), unit_price: String(line.unit_price) }));
}
function amount(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value.replace(",", "."));
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1e9 ? parsed : null;
}
const round = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const PERCENTAGE_COMPLEMENTS = new Set(["complement", "auxiliary", "indirect"]);

// Verified on the retained CYPE fichas: meios auxiliares (auxiliary) is a percentage of the
// direct cost; custos indiretos (indirect) is a percentage of the subtotal after that line.
// A single trailing `complement` stays on the direct cost. Missing inputs stay null.
function percentagePattern(source: CompositionLine[]): "none" | "complement" | "cype_pair" | "invalid" {
  const start = source.findIndex((line) => line.unit === "%");
  if (start < 0) return "none";
  const tail = source.slice(start);
  if (tail.some((line) => line.unit !== "%")) return "invalid";
  if (tail.length === 1 && tail[0].code === "complement") return "complement";
  if (tail.length === 2 && tail[0].code === "auxiliary" && tail[1].code === "indirect") return "cype_pair";
  return "invalid";
}

export function calculateComposition(source: CompositionLine[], edits: CompositionEdit[]) {
  const pattern = percentagePattern(source);
  let complete = source.length > 0 && source.length === edits.length && pattern !== "invalid";
  let running: number | null = 0;
  const lines = source.map((line, i) => {
    const edit = edits[i];
    const quantity = edit && edit.code === line.code ? amount(edit.quantity) : null;
    if (quantity === null) complete = false;
    if (line.unit !== "%") {
      const price = edit ? amount(edit.unit_price) : null;
      if (price === null) complete = false;
      const partial = quantity !== null && price !== null ? round(quantity * price) : null;
      if (partial === null || running === null) running = null;
      else running = round(running + partial);
      return { quantity, price, partial };
    }
    const allowed = PERCENTAGE_COMPLEMENTS.has(line.code) && quantity !== null && quantity <= 100;
    if (!allowed) complete = false;
    const price = allowed ? running : null;
    const partial = price !== null && quantity !== null ? round(price * quantity / 100) : null;
    if (partial === null || price === null) running = null;
    else running = round(price + partial);
    return { quantity, price, partial };
  });
  const total = complete && lines.every((line) => line.partial !== null)
    ? round(lines.reduce((sum, line) => sum + line.partial!, 0))
    : null;
  return { lines, complete: complete && total !== null, total };
}

export function compareComposition(source:CompositionLine[],edits:CompositionEdit[],blocked=false) {
 const origin=calculateComposition(source,compositionEdits(source));
 const current=calculateComposition(source,edits);
 const lines=source.map((line,index)=>{
  const value=current.lines[index];
  const quantityChanged=value.quantity!==line.quantity;
  const priceChanged=line.unit!=="%" && value.price!==line.unit_price;
  const partialChanged=value.partial!==origin.lines[index].partial;
  return {quantityChanged,priceChanged,partialChanged,changed:quantityChanged || priceChanged || partialChanged,
   difference:value.partial!==null && origin.lines[index].partial!==null
    ? round(value.partial-origin.lines[index].partial!) : null};
 });
 const total=blocked ? null : current.total;
 const difference=total!==null && origin.total!==null ? round(total-origin.total) : null;
 return {lines,originTotal:origin.total,currentTotal:total,difference,
  percentage:difference!==null && origin.total!==null && origin.total!==0 ? round(difference/origin.total*100) : null,
  changedCount:lines.filter(line=>line.changed).length};
}
export function configurationChanges(origin:Record<string,string>,current:Record<string,string>) {
 function equal(a:string,b:string) {
  if(a===b)return true;
  return !!a.trim() && !!b.trim() && Number.isFinite(Number(a.replace(",",".")))
   && Number.isFinite(Number(b.replace(",","."))) && Number(a.replace(",","."))===Number(b.replace(",","."));
 }
 return [...new Set([...Object.keys(origin),...Object.keys(current)])]
  .filter(key=>!equal(origin[key]??"",current[key]??""))
  .map(key=>({key,original:origin[key]??"",current:current[key]??""}));
}
